import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@codes/ui-kit/components/ui/button';
import { Card } from '@codes/ui-kit/components/ui/card';
import { Image } from '@codes/ui-kit/components/ui/image';
import { ProductView } from '@lemoncloud/codes-goods-api';
import { ListResult } from '@lemoncloud/codes-goods-api/dist/cores/types';
import { useQueryClient } from '@tanstack/react-query';
import {
    AIStudioDialog,
    ChildrenSheet,
    ProductCard,
    ServiceEventLogDialog,
    ServiceSettingsDialog,
    SortSelect,
    StereoSelect,
} from '@web/app/features/home/components';
import {
    useDeleteWithConfirmation,
    useDialogState,
    useProductFiltering,
    useProductService,
    useWorkspaceService,
} from '@web/app/features/home/hooks';
import { getStereoDisplayName, useSelectedStore, useWorkspaceNavigation } from '@web/app/shared';
import { PageContainer } from '@web/app/shared/components/PageContainer';
import { Plus } from 'lucide-react';

import { ListEmptyImage } from '@codes/assets';
import { productsKey, useAutoDeployProduct, useDeleteProduct } from '@codes/product';
import { SharedSpinner, toast } from '@codes/ui-kit';

export const ProductListPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { project, workspace } = useSelectedStore();
    const { myProducts, myProductsQueryResult, areMyProductsEmpty } = useProductService();

    const isProWorkspace = workspace && workspace.stereo === 'pro';
    const isLoading = myProductsQueryResult.isFetching || myProductsQueryResult.isRefetching;
    const isInitialLoading = myProductsQueryResult.isLoading && !myProductsQueryResult.data;

    // Use custom hooks for filtering and dialog state
    const { sortType, setSortType, stereo, setStereo, stereos, filteredProducts, filteredStereos } =
        useProductFiltering(myProducts);

    const settingsDialog = useDialogState<string>();
    const eventLogDialog = useDialogState<string>();
    const [isAIStudioDialogOpen, setAIStudioDialogOpen] = useState(false);
    const [childrenSheetProduct, setChildrenSheetProduct] = useState<ProductView | null>(null);

    const { workspaceFromServer } = useWorkspaceService();

    const { navigateToProjectPath } = useWorkspaceNavigation({ workspace: workspaceFromServer, project });

    const addServiceButtonTapped = () => {
        navigateToProjectPath('/catalogs');
    };

    const viewServiceSettingButtonTapped = (productId?: string) => {
        if (!productId) {
            return;
        }
        settingsDialog.open(productId);
    };

    const viewServiceEventLogButtonTapped = (productId?: string) => {
        if (!productId) {
            return;
        }
        eventLogDialog.open(productId);
    };

    const viewProductDetailButtonTapped = (product: ProductView) => {
        if (!workspace || !project) {
            return;
        }
        navigate(`/w/${workspace.id}/p/${project.id}/products/${product.id}`);
    };

    const { mutateAsync: deleteProduct } = useDeleteProduct();
    const { mutateAsync: autoDeployProduct, isPending: isAutoDeploying, currentProductId } = useAutoDeployProduct();
    const queryClient = useQueryClient();

    const { confirmAndDelete } = useDeleteWithConfirmation({
        onDelete: deleteProduct,
        confirmMessage: t('serviceSetting.confirmServiceDeletion'),
        onSuccess: deletedProduct => {
            toast({
                description: t('serviceSetting.deleteSuccess', { productName: deletedProduct?.name }),
            });

            queryClient.setQueryData<ListResult<ProductView>>(productsKey.list({ projectId: project?.id }), oldData => {
                if (!oldData) {
                    return oldData;
                }
                return {
                    ...oldData,
                    list: oldData.list.filter(product => product.id !== deletedProduct.id),
                    data: oldData.data.filter(product => product.id !== deletedProduct.id),
                    total: (oldData.total || 1) - 1,
                };
            });
        },
    });

    const viewServiceDeleteButtonTapped = async (productId?: string) => {
        if (!productId) {
            return;
        }
        await confirmAndDelete(productId);
    };

    const reDeployProductButtonTapped = async (productId?: string) => {
        if (!productId) {
            return;
        }

        try {
            await autoDeployProduct(productId);

            queryClient.setQueryData<ListResult<ProductView>>(productsKey.list({ projectId: project?.id }), oldData => {
                if (!oldData) {
                    return oldData;
                }
                return {
                    ...oldData,
                    list: oldData.list.map(product =>
                        product.id === productId ? { ...product, status: 'idle', progress$: undefined } : product
                    ),
                    data: oldData.data.map(product =>
                        product.id === productId ? { ...product, status: 'idle', progress$: undefined } : product
                    ),
                };
            });

            toast({
                title: t('product.frontend.deploy.requestSuccess'),
                description: t('product.frontend.deploy.requestSuccessDescription', {
                    productName: myProducts.find(p => p.id === productId)?.name || '',
                }),
            });
        } catch (e) {
            console.error(e, 'Error while redeploying product');
        }
    };

    const openChildrenSheet = (product: ProductView) => {
        setChildrenSheetProduct(product);
    };

    const closeChildrenSheet = () => {
        setChildrenSheetProduct(null);
    };

    // Loading state
    if (isInitialLoading) {
        return (
            <PageContainer>
                <div className="flex items-center justify-between mb-4">
                    <div className="text-lg font-semibold font-display">{t('product.myService')}</div>
                    <div className="flex gap-2">
                        {isProWorkspace && (
                            <Button onClick={() => setAIStudioDialogOpen(true)} variant="outline" className="gap-1.5">
                                <Plus size={16} /> {t('aiStudio.add')}
                            </Button>
                        )}
                        <Button onClick={addServiceButtonTapped} className="gap-1.5">
                            <Plus size={16} /> {t('project.addService')}
                        </Button>
                    </div>
                </div>
                <div className="flex items-center gap-3 mb-6">
                    <SortSelect value={sortType} onChange={setSortType} />
                    <StereoSelect value={stereo} onChange={setStereo} stereos={stereos} />
                    <div className="text-text-400">|</div>
                    <div className="text-point text-sm font-medium">Total --</div>
                </div>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] max-xs:grid-cols-1 gap-4">
                    {[...Array(6)].map((_, i) => (
                        <Card key={i} className="w-full p-5 h-[280px] flex flex-col">
                            <div className="animate-pulse flex-1 flex flex-col">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="w-16 h-6 bg-default rounded-full"></div>
                                    <div className="w-6 h-6 bg-default rounded"></div>
                                </div>
                                <div className="flex items-center gap-4 mb-4 flex-1">
                                    <div className="w-[50px] h-[50px] bg-default rounded-[14px] flex-shrink-0"></div>
                                    <div className="flex-1">
                                        <div className="h-4 bg-default rounded mb-2 w-2/3"></div>
                                        <div className="h-5 bg-default rounded w-1/2"></div>
                                    </div>
                                </div>
                                <div className="space-y-2 mb-4">
                                    <div className="h-3 bg-default rounded w-full"></div>
                                    <div className="h-3 bg-default rounded w-3/4"></div>
                                </div>
                                <div className="flex gap-2 mt-auto">
                                    <div className="h-6 bg-default rounded w-16"></div>
                                    <div className="h-6 bg-default rounded w-16"></div>
                                    <div className="h-6 bg-default rounded w-16"></div>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            </PageContainer>
        );
    }

    // Error state
    if (myProductsQueryResult.error) {
        return (
            <PageContainer>
                <div className="flex items-center justify-between mb-4">
                    <div className="text-lg font-semibold font-display">{t('product.myService')}</div>
                    <div className="flex gap-2">
                        {isProWorkspace && (
                            <Button onClick={() => setAIStudioDialogOpen(true)} variant="outline" className="gap-1.5">
                                <Plus size={16} /> {t('aiStudio.add')}
                            </Button>
                        )}
                        <Button onClick={addServiceButtonTapped} className="gap-1.5">
                            <Plus size={16} /> {t('project.addService')}
                        </Button>
                    </div>
                </div>
                <div className="flex flex-col items-center justify-center py-20">
                    <div className="text-error text-lg font-medium mb-4">Failed to load services</div>

                    <Button
                        onClick={() => myProductsQueryResult.refetch()}
                        variant="outline"
                        disabled={myProductsQueryResult.isRefetching}
                    >
                        {isLoading ? <SharedSpinner /> : t('project.error.retry')}
                    </Button>
                </div>
            </PageContainer>
        );
    }

    // Empty state
    if (areMyProductsEmpty || filteredProducts.length === 0) {
        return (
            <PageContainer>
                <div className="text-lg font-semibold font-display">{t('product.myService')}</div>
                <div className="flex-1 flex flex-col justify-center items-center mt-10">
                    <Image className="max-w-[140px]" src={ListEmptyImage} />
                    <div className="text-lg font-semibold font-display text-text mb-3 mt-5">
                        {t('product.noServices')}
                    </div>
                    <div className="text-sm text-text-500 mb-5 text-center">{t('product.addServicePrompt')}</div>
                    <div className="flex gap-2">
                        {isProWorkspace && (
                            <Button onClick={() => setAIStudioDialogOpen(true)} variant="outline" className="gap-1.5">
                                <Plus size={16} />
                                {t('aiStudio.add')}
                            </Button>
                        )}
                        <Button onClick={addServiceButtonTapped} className="gap-1.5">
                            <Plus size={16} />
                            {t('project.addService')}
                        </Button>
                    </div>
                </div>
                <AIStudioDialog open={isAIStudioDialogOpen} onOpenChange={setAIStudioDialogOpen} />
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <div className="flex items-center justify-between mb-4">
                <div className="text-lg font-semibold font-display">{t('product.myService')}</div>
                <div className="flex gap-2">
                    {isProWorkspace && (
                        <Button onClick={() => setAIStudioDialogOpen(true)} variant="outline" className="gap-1.5">
                            <Plus size={16} /> {t('aiStudio.add')}
                        </Button>
                    )}
                    <Button onClick={addServiceButtonTapped} className="gap-1.5">
                        <Plus size={16} /> {t('project.addService')}
                    </Button>
                </div>
            </div>
            <div className="flex items-center gap-3 mb-6">
                <SortSelect value={sortType} onChange={setSortType} />
                <StereoSelect value={stereo} onChange={setStereo} stereos={stereos} />
                <div className="h-4 w-px bg-border" />
                <div className="text-xs text-text-500 font-medium">Total {filteredProducts?.length || 0}</div>
            </div>

            <div className="flex flex-col gap-y-8">
                {filteredStereos.map(stereo => {
                    const stereoProducts = filteredProducts.filter(product => product.catalog$?.stereo === stereo);

                    return (
                        <div key={stereo} className="flex flex-col gap-y-4">
                            <div className="flex items-center gap-2">
                                <div className="text-xs font-semibold text-point uppercase tracking-wider">
                                    {getStereoDisplayName(stereo)}
                                </div>
                                <div className="text-xs text-text-400">({stereoProducts.length})</div>
                                <div className="flex-1 h-px bg-border ml-2" />
                            </div>
                            <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] max-xs:grid-cols-1 gap-4">
                                {stereoProducts.map(product => {
                                    const isAIStudio = product.catalog$?.stereo === 'aistudio';

                                    return (
                                        <ProductCard
                                            key={product.id}
                                            product={product}
                                            isDeploying={isAutoDeploying && currentProductId === product.id}
                                            onProductClick={() => viewProductDetailButtonTapped(product)}
                                            onSettingsClick={() => viewServiceSettingButtonTapped(product.id)}
                                            onEventLogsClick={() => viewServiceEventLogButtonTapped(product.id)}
                                            onRedeploy={() => reDeployProductButtonTapped(product.id)}
                                            onDelete={() => viewServiceDeleteButtonTapped(product.id)}
                                            showChildrenButton={isAIStudio}
                                            onViewChildrenClick={() => openChildrenSheet(product)}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            {settingsDialog.isOpen && settingsDialog.data && (
                <ServiceSettingsDialog
                    open={settingsDialog.isOpen}
                    onOpenChange={open => !open && settingsDialog.close()}
                    productId={settingsDialog.data}
                />
            )}

            {eventLogDialog.isOpen && eventLogDialog.data && (
                <ServiceEventLogDialog
                    productId={eventLogDialog.data}
                    open={eventLogDialog.isOpen}
                    setOpen={open => !open && eventLogDialog.close()}
                />
            )}

            <AIStudioDialog open={isAIStudioDialogOpen} onOpenChange={setAIStudioDialogOpen} />

            {/* Children Sheet */}
            {childrenSheetProduct && (
                <ChildrenSheet
                    parentProduct={childrenSheetProduct}
                    open={!!childrenSheetProduct}
                    onOpenChange={open => !open && closeChildrenSheet()}
                    isAutoDeploying={isAutoDeploying}
                    currentProductId={currentProductId}
                    onProductClick={viewProductDetailButtonTapped}
                    onSettingsClick={viewServiceSettingButtonTapped}
                    onEventLogsClick={viewServiceEventLogButtonTapped}
                    onRedeploy={reDeployProductButtonTapped}
                    onDelete={viewServiceDeleteButtonTapped}
                />
            )}
        </PageContainer>
    );
};
